import type {
  CorporateBookingForm,
  CheckoutField,
  CheckoutPrefill,
  CheckoutPrefillTraveler,
  CheckoutTravelerDraft,
} from '@voll-intelligence/genui-contract';
import { toTravelerEdits } from './checkout_submit.js';
import type { AllocationDraft } from '@voll-intelligence/intelligence-ui-kit';

/** Os campos de identidade que o card 1click pede em «Complete seus dados»: os
 *  do manifesto que o PERFIL não tem — o que o servidor flaga em
 *  `missingRequired` e, para o telefone (que nunca entra lá), a falta dele em
 *  ficha. Lido do form, não do rascunho: a seção não pode sumir enquanto a
 *  pessoa digita o valor que a completa. Ordem do manifesto. */
export function missingTravelerFields(
  fields: readonly CheckoutField[],
  form: CorporateBookingForm | undefined,
): CheckoutField[] {
  if (!form) return [];
  const missing = new Set<string>(form.traveler?.missingRequired ?? []);
  return fields.filter((field) => {
    if (field.group !== 'traveler') return false;
    if (field.kind === 'phone') return !form.traveler?.phone;
    return missing.has(field.kind);
  });
}

interface HandoffSlice {
  readonly traveler: { readonly id: string };
  readonly form?: CorporateBookingForm;
  readonly allocations: readonly AllocationDraft[];
  readonly travelerDraft: CheckoutTravelerDraft;
}

function handoffTraveler(slice: HandoffSlice): CheckoutPrefillTraveler {
  // O prefill leva uma linha só: o 1click não rateia (a tela cheia, sim).
  const { companyId = '', costCenterId = '', projectId = '' } = slice.allocations[0] ?? {};
  const edits = toTravelerEdits(slice.travelerDraft, slice.form?.traveler);
  return {
    id: slice.traveler.id,
    ...(companyId && costCenterId
      ? { allocation: { companyId, costCenterId, ...(projectId ? { projectId } : {}) } }
      : {}),
    ...(Object.keys(edits).length > 0 ? { edits } : {}),
  };
}

/** O que o card já tem na tela, no formato do `prefill` que o drawer entende —
 *  é assim que «Revisar dados» abre a tela cheia com as escolhas da
 *  pessoa, e não com as do perfil. O drawer revalida tudo contra os forms
 *  frescos (`allocationFromPrefill`, `filterPrefillEdits`), então um id que
 *  envelheceu cai no seed de sempre em vez de virar seleção fantasma. */
export function handoffPrefill(state: {
  readonly travelers: readonly HandoffSlice[];
  readonly motiveId: string;
  readonly justificationId?: string;
  readonly observation: string;
}): CheckoutPrefill {
  return {
    ...(state.motiveId ? { motiveId: state.motiveId } : {}),
    ...(state.justificationId ? { justificationId: state.justificationId } : {}),
    ...(state.observation ? { observation: state.observation } : {}),
    travelers: state.travelers.map(handoffTraveler),
  };
}
